import { useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux"
import { Box, Grid, Typography, Stack, Skeleton } from "@mui/material"
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Link from '@mui/material/Link'

import MainContainer from "../../components/Container/MainContainer"
import BlockContainer from "../../components/Container/BlockContainer"
import AppContentHeader from "../../components/AppContentHeader"
import StatementSection from "../../components/StatementSection"
import BaseImage from "../../components/BaseImage"
import BaseLink from "../../components/BaseLink"
import ReportReviewBottomNav from "../../components/ReportReviewBottomNav";
import BaseAccordion from "../../components/BaseAccordion";
import GeneButtonList from "../../components/GeneButtonList";
import { openModal } from "../../store";
import { HOW_IT_WORKS_ARTICLES, REDIRECT_PATH_STORAGE_ITEM, REPORTS_VIDEO_LIST } from "../../constants";
import { findMemberByUserId } from "../../utils/member-lookup";

const selectedArticle = HOW_IT_WORKS_ARTICLES.find(item => item.label === 'Nutrition');

const NutritionReport = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id: patient_id } = useParams()
  const members = useSelector(state => state.members)
  const selectedPatient = useMemo(() => findMemberByUserId(members, patient_id), [members, patient_id])
  const loadingAuth = useSelector(state => state.loadingAuth)
  const reports = useSelector(state => state.patientReports)
  const nutritionData = useMemo(() => reports && reports.find(item => item.name === 'Nutrition'), [reports])
  const openVideoModal = (e) => {
    e.stopPropagation()
    dispatch(
      openModal({
        currentModal: 'REPORT_VIDEO_SUMMARY',
        modalProps: {
          videoUrl: REPORTS_VIDEO_LIST['Nutrition']
        }
    }))
  }

  const getReportFileName = () => {
    return selectedPatient?.user?.kits?.[0]?.accessionId + '-Nutrition.pdf'
  }
  const sumscores = useMemo(() => {
    const sumscoresData = nutritionData?.data?.content?.nutrition?.sumscores;
    if (sumscoresData) {
      const sumscoresArray = Object.keys(sumscoresData).map(key => sumscoresData[key])
      return sumscoresArray
    } else return []
  }, [nutritionData])

  const riskCounts = useMemo(() => {
    if (sumscores && sumscores.length > 0) {
      let highRisk = 0
      let moderateRisk = 0
      let lowRisk = 0
      sumscores.forEach(item => {
        if (item?.rating === 3) highRisk += 1
        else if (item?.rating === 2) moderateRisk++
        else if (item?.rating === 1) lowRisk++
      })
      
      return [
        {
          score: highRisk || 0,
          level: 'high',
          label: 'High Risk \ngenetic traits',
          color: 'primary.red'
        },
        {
          score: moderateRisk || 0,
          level: 'moderate',
          label: 'Moderate Risk \ngenetic traits',
          color: 'primary.yellow'
        },
        {
          score: lowRisk || 0,
          level: 'low',
          label: 'Low Risk \ngenetic traits',
          color: 'primary.darkGreen'
        },
      ]
    }
    return []
  }, [sumscores])

  const sumscoresByCategory = useMemo(() => {
    if (sumscores && sumscores.length > 0) {
      const categoryObj = sumscores.reduce((catsSoFar, current) => {
        let groupKey = current['category']
        if (!catsSoFar[groupKey]) catsSoFar[groupKey] = [];
        catsSoFar[groupKey].push(current);
        return catsSoFar;
      }, {});
      
      const categoryObjArray = Object.keys(categoryObj).map(key => {
        let highRisk = 0;
        let moderateRisk = 0;
        let lowRisk = 0;
        const categoryItems = categoryObj[key]
        if (categoryItems && categoryItems.length > 0) {
          categoryItems.forEach(item => {
            if (item?.rating === 3) highRisk += 1
            else if (item?.rating === 2) moderateRisk++
            else if (item?.rating === 1) lowRisk++
          })
        }

        return {
          highRisk,
          moderateRisk,
          lowRisk,
          category: key
        }
      })
      return categoryObjArray.sort((a, b) => a.category.localeCompare(b.category))
    } 
    return []
  }, [sumscores])

  const handleClickRisk = (type, filterKey) => {
    navigate(`/patient/${patient_id}/nutrition-category/${type}/${filterKey}`)
  }

  const handleClickHelp = () => {
    dispatch(openModal({
      currentModal: 'GUIDANCE_LEVEL_MODAL',
    }))
  }

  const handleBack = () => {
    const savedRedirectPath = decodeURIComponent(localStorage.getItem(REDIRECT_PATH_STORAGE_ITEM));
    const currentPath = decodeURIComponent(window.location.pathname);
    if(savedRedirectPath === currentPath) {
      localStorage.removeItem(REDIRECT_PATH_STORAGE_ITEM);
      navigate('/')
    } else {
      navigate(-1)
    }
  }
  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden',
        background: "linear-gradient(180deg, #F6F9FC 0%, #C6E7FF 100%)",
      }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Box className='content-header-wave'></Box>
        <BlockContainer
          sx={{
            position: 'relative',
            padding: '16px 20px',
            zIndex: 10,
            mt: '-90px'
          }}
        >
          <AppContentHeader 
            pdfDownloadUrl={nutritionData?.pdfUrl} 
            hasBackLink
            onBack={handleBack}
            pdfName={getReportFileName()}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              padding: '50px 0 38px',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mt: '50px'
              }}
            >
              <Typography variant="h1">
                Nutrition report
              </Typography>
              <StatementSection dataUpdatedAt={nutritionData?.dataUpdatedAt} />
            </Box>
            {loadingAuth ? (
              <Skeleton variant="rounded" width={160} height={160} />
            ) : (
              <BaseImage
                src={nutritionData?.icon}
                sx={{
                  width: '160px',
                  height: '160px',
                  position: 'absolute',
                  right: -20,
                  top: { xs: 50, md: 100 },
                  opacity: 0.4
                }}
              />
            )}
          </Box>
        </BlockContainer>
        <BlockContainer
          sx={{
            px: '20px',
            mb: '20px',
            position: 'relative',
            zIndex: 10
          }}
        >
          <Stack spacing={'40px'} direction={'column'}>
            <Box
              sx={{
                padding: '20px 16px',
                boxShadow: '0px 2px 10px 0px #5B87EF33',
                borderRadius: '8px',
                bgcolor: 'white'
              }}
            >
              <Box
                sx={{
                  mb: '16px'
                }}
              >
                <Typography variant="h2">
                  Summary
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: '16px'
                  }}
                >
                  <Typography variant="body" color="primary.darkGray">
                    Your results based on guidance levels
                  </Typography>
                  <BaseLink underline={true} onClick={handleClickHelp}>
                    <Typography variant="body">
                      What are guidance levels?
                    </Typography>
                  </BaseLink>
                </Box>
              </Box>
              <Grid container spacing={2}>
                {loadingAuth && (
                  <>
                  {new Array(3).fill(null).map((_, idx) => (
                    <Grid item xs={12} md={4} key={idx}>
                      <Skeleton variant="rounded" width={'100%'} height={106} />
                    </Grid>
                  ))}
                  </>
                )}
                {!loadingAuth && riskCounts.map((risk => (
                  <Grid item xs={12} lg={4} key={risk.level}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        padding: '16px',
                        border: '3px solid #fff',
                        borderTopColor: risk.color,
                        borderLeftColor: risk.color,
                        bgcolor: 'white',
                        minHeight: '106px',
                        cursor: 'pointer',
                        boxShadow: '0px 2px 10px 0px #5B87EF33',

                        '&:active': {
                          borderColor: risk.color
                        }
                      }}
                      onClick={() => handleClickRisk('guidance', risk.level)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Typography variant="h1" fontSize={'40px'}>
                            {risk.score}
                          </Typography>
                          <Typography 
                            variant="h4" 
                            sx={{
                              ml: '16px',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {risk.label}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            cursor: 'pointer',
                            mr: '16px'
                          }}
                        >
                          <ArrowForwardIosIcon 
                            sx={{
                              color: 'primary.black',
                              width: '18px'
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )))}
              </Grid>
              <Box
                sx={{
                  bgcolor: 'primary.lightGray',
                  height: '1px',
                  width: '100%',
                  my: '16px'
                }}
              >
              </Box>
              <Box>
                <BaseAccordion
                  isExpand={false}
                  isExpandIcon
                  item={{
                    title: 'What was tested',
                  }}
                  sx={{
                    bgcolor: 'primary.lightGray',
                    boxShadow: '0px'
                  }}
                >
                  <Box sx={{mb: '14px'}}>
                    <Link sx={{cursor: 'pointer'}} onClick={openVideoModal}>
                      <Typography variant='body'>
                        Video Summary
                      </Typography>
                    </Link>
                  </Box>
                  <Typography
                    variant="body"
                    color="primary.darkGray"
                    whiteSpace='pre-wrap'
                  >
                    {selectedArticle?.description}
                  </Typography>
                  {selectedArticle?.genes && (
                    <Typography
                      variant="body"
                      color="primary.darkGray"
                      whiteSpace='pre-wrap'
                    >
                      <GeneButtonList genes={selectedArticle.genes} styleProps={{
                        fontSize: '15px',
                        fontWeight: '500',
                      }}/>
                    </Typography>
                  )}
                </BaseAccordion>
              </Box>
            </Box>
            <Box
              sx={{
                borderRadius: '8px',
                padding: '20px 16px',
                bgcolor: 'white',
                boxShadow: '0px 2px 10px 0px #5B87EF33'
              }}
            >
              <Stack
                direction={'column'}
                spacing={'16px'}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Typography variant="h2">
                    View by category
                  </Typography>
                </Box>
                <Box>
                  <Grid container spacing={2}>
                    {sumscoresByCategory.map(((item, idx) => (
                      <Grid item xs={12} lg={6} key={idx}>
                        <Box
                          sx={{
                            borderRadius: '8px',
                            border: '1px solid',
                            borderColor: 'primary.lightGray',
                            padding: '16px',
                            boxShadow: '0px 2px 10px 0px #5B87EF1A',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onClick={() => handleClickRisk('category', item?.category)}
                        >
                          <Box>
                            <Typography variant="h3">
                              {item?.category}
                            </Typography>
                            <Stack
                              direction={'row'}
                              spacing={'8px'}
                              mt='8px'
                            >
                              <Box
                                sx={{
                                  borderRadius: '4px',
                                  padding: '1px 7px',
                                  bgcolor: 'primary.lightRed'
                                }}
                              >
                                <Typography variant="subText" color='primary.darkGray'>{item?.highRisk} High</Typography>
                              </Box>
                              <Box
                                sx={{
                                  borderRadius: '4px',
                                  padding: '1px 7px',
                                  bgcolor: 'primary.lightYellow'
                                }}
                              >
                                <Typography variant="subText" color='primary.darkGray'>{item?.moderateRisk} Moderate</Typography>
                              </Box>
                              <Box
                                sx={{
                                  borderRadius: '4px',
                                  padding: '1px 7px',
                                  bgcolor: 'primary.lightGreen'
                                }}
                              >
                                <Typography variant="subText" color='primary.darkGray'>{item?.lowRisk} Low</Typography>
                              </Box>
                            </Stack>
                          </Box>
                          <Box
                            sx={{
                              cursor: 'pointer',
                              mr: '16px'
                            }}
                          >
                            <ArrowForwardIosIcon 
                              sx={{
                                color: 'primary.mainBlue',
                                width: '18px'
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    )))}
                  </Grid>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </BlockContainer>
      </Box>
      <ReportReviewBottomNav reportDetail={nutritionData} />
    </MainContainer>
  )
}

export default NutritionReport